import React from 'react';

import { User } from '../../../apis/types';
import { BUSINESS_FIELD_OPTIONS } from '../../../constants/businessFieldOptions';

interface UserInfoDisplayProps {
  user: User;
  auth0User: { email?: string; email_verified?: boolean };
}

const UserInfoDisplay: React.FC<UserInfoDisplayProps> = ({
  user,
  auth0User,
}) => {
  return (
    <div className='w-full space-y-4'>
      {/* Warning Message for Missing Fields */}
      {(!user.name || !user.companyName || !user.businessField) && (
        <div className='bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-4'>
          <div className='flex items-start gap-3'>
            <svg
              className='w-5 h-5 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5'
              fill='currentColor'
              viewBox='0 0 20 20'
            >
              <path
                fillRule='evenodd'
                d='M8.485 2.495c.673-1.167 2.357-1.167 3.03 0l6.28 10.875c.673 1.167-.17 2.625-1.516 2.625H3.72c-1.347 0-2.189-1.458-1.515-2.625L8.485 2.495zM10 5a.75.75 0 01.75.75v3.5a.75.75 0 01-1.5 0v-3.5A.75.75 0 0110 5zm0 9a1 1 0 100-2 1 1 0 000 2z'
                clipRule='evenodd'
              />
            </svg>
            <div className='flex-1'>
              <h3 className='text-sm font-medium text-amber-800 dark:text-amber-200 mb-1'>
                Complete your profile for better results
              </h3>
              <p className='text-sm text-amber-700 dark:text-amber-300'>
                {(() => {
                  const missingFields = [];
                  if (!user.name) missingFields.push('name');
                  if (!user.companyName) missingFields.push('company name');
                  if (!user.businessField) missingFields.push('business field');

                  if (missingFields.length === 3) {
                    return 'Please add your name, company name, and business field. These values are commonly used in your invoices and will help generate more accurate bookings.';
                  } else if (missingFields.length === 2) {
                    return `Please add your ${missingFields[0]} and ${missingFields[1]}. These values are commonly used in your invoices and will help generate more accurate bookings.`;
                  } else {
                    return `Please add your ${missingFields[0]}. This value is commonly used in your invoices and will help generate more accurate bookings.`;
                  }
                })()}
              </p>
            </div>
          </div>
        </div>
      )}

      <div className='grid grid-cols-1 md:grid-cols-2 gap-6'>
        {/* Left Column */}
        <div className='space-y-3'>
          <div className='border-b border-gray-200 dark:border-gray-700 pb-3'>
            <span className='block text-sm font-medium text-gray-500 dark:text-gray-400 mb-1'>
              Name
            </span>
            <p className='text-gray-900 dark:text-white'>
              {user.name || (
                <span className='text-gray-400 italic'>Not set</span>
              )}
            </p>
          </div>

          <div className='border-b border-gray-200 dark:border-gray-700 pb-3'>
            <span className='block text-sm font-medium text-gray-500 dark:text-gray-400 mb-1'>
              Email
            </span>
            <p className='text-gray-900 dark:text-white'>
              {auth0User.email || (
                <span className='text-gray-400 italic'>Not set</span>
              )}
            </p>
          </div>

          <div className='pb-3'>
            <span className='block text-sm font-medium text-gray-500 dark:text-gray-400 mb-1'>
              Email Verified
            </span>
            <div className='flex items-center'>
              <span
                className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                  auth0User.email_verified
                    ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                    : 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200'
                }`}
              >
                {auth0User.email_verified ? 'Verified' : 'Not Verified'}
              </span>
            </div>
          </div>
        </div>

        {/* Right Column */}
        <div className='space-y-3'>
          <div className='border-b border-gray-200 dark:border-gray-700 pb-3'>
            <span className='block text-sm font-medium text-gray-500 dark:text-gray-400 mb-1'>
              Company Name
            </span>
            <p className='text-gray-900 dark:text-white'>
              {user.companyName || (
                <span className='text-gray-400 italic'>Not set</span>
              )}
            </p>
          </div>

          <div className='border-b border-gray-200 dark:border-gray-700 pb-3'>
            <span className='block text-sm font-medium text-gray-500 dark:text-gray-400 mb-1'>
              Business Field / Industry
            </span>
            <p className='text-gray-900 dark:text-white'>
              {user.businessField ? (
                BUSINESS_FIELD_OPTIONS.find(
                  opt => opt.value === user.businessField
                )?.label || user.businessField
              ) : (
                <span className='text-gray-400 italic'>Not set</span>
              )}
            </p>
          </div>

          <div className='pb-3'>
            <span className='block text-sm font-medium text-gray-500 dark:text-gray-400 mb-1'>
              Language Preference
            </span>
            <p className='text-gray-900 dark:text-white'>
              {user.preferredLanguage || (
                <span className='text-gray-400 italic'>Not set</span>
              )}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UserInfoDisplay;
